import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang, pageKeyFromPath, ROUTES, LANGS } from '../../i18n';

const DEFAULT_TITLES = {
  es: 'Cadenia | Visibilidad e Inteligencia en la Cadena de Suministro',
  en: 'Cadenia | Supply Chain Visibility & Intelligence',
};

const OG_LOCALES = { es: 'es_ES', en: 'en_US' };

const PageSEO = ({ title, description, keywords, type = 'WebPage', jsonLd }) => {
  const location = useLocation();
  const { lang } = useLang();
  const fullUrl = `https://www.cadenia.eu${location.pathname}`;
  const fullTitle = title ? `${title} | Cadenia` : DEFAULT_TITLES[lang];

  useEffect(() => {
    document.title = fullTitle;

    updateMeta('name', 'description', description);
    if (keywords) {
      updateMeta('name', 'keywords', keywords);
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    // hreflang: alternates de la página actual en cada idioma (x-default = español)
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const pageKey = pageKeyFromPath(location.pathname);
    if (pageKey) {
      const addAlternate = (hreflang, path) => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        el.setAttribute('href', `https://www.cadenia.eu${path}`);
        document.head.appendChild(el);
      };
      LANGS.forEach((l) => addAlternate(l, ROUTES[pageKey][l]));
      addAlternate('x-default', ROUTES[pageKey].es);
    }

    // Open Graph
    updateMeta('property', 'og:title', fullTitle);
    updateMeta('property', 'og:description', description);
    updateMeta('property', 'og:url', fullUrl);
    updateMeta('property', 'og:type', 'website');
    updateMeta('property', 'og:site_name', 'Cadenia');
    updateMeta('property', 'og:locale', OG_LOCALES[lang]);

    // Twitter
    updateMeta('name', 'twitter:card', 'summary_large_image');
    updateMeta('name', 'twitter:title', fullTitle);
    updateMeta('name', 'twitter:description', description);

    // JSON-LD
    const existingJsonLd = document.getElementById('page-seo-jsonld');
    if (existingJsonLd) existingJsonLd.remove();

    const structuredData = jsonLd || {
      "@context": "https://schema.org",
      "@type": type,
      "name": title,
      "description": description,
      "url": fullUrl,
      "inLanguage": lang,
      "provider": {
        "@type": "Organization",
        "name": "Cadenia",
        "url": "https://www.cadenia.eu"
      }
    };

    const script = document.createElement('script');
    script.id = 'page-seo-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('page-seo-jsonld');
      if (el) el.remove();
    };
  }, [fullTitle, description, keywords, fullUrl, type, jsonLd, lang, location.pathname]);

  return null;
};

const updateMeta = (attr, name, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export default PageSEO;
